import { createRoot } from 'react-dom/client';

import browser from '@openclassrooms/utils/browser';

import { ajaxGet } from 'Modules/ajax';
import AppProviders from 'Modules/webapp/components/AppProviders';

import AutocompleteFromDom, { DIRECTIVES_VALUES } from './components/AutocompleteFromDom';

function mapDefaultValue(defaultValue = {}) {
  return {
    id: defaultValue.data,
    value: defaultValue.label,
  };
}

function getDefaultValue(properties, directiveValue) {
  switch (directiveValue) {
    case DIRECTIVES_VALUES.SIMPLE:
      return mapDefaultValue(properties.defaultValue);

    case DIRECTIVES_VALUES.MULTIPLE:
      return (properties.defaultValues || []).map(mapDefaultValue);

    default:
      return null;
  }
}

function getComponentProps(
  directiveValue,
  input,
  dataSource,
  defaultValue,
  onChange,
  onClear,
  suggestionRenderer,
  otherProps
) {
  const commonProps = {
    input,
    dataSource,
    suggestionRenderer,
    ...otherProps,
  };

  switch (directiveValue) {
    case DIRECTIVES_VALUES.SIMPLE:
      return {
        ...commonProps,
        onClear,
        defaultValue,
        onSelectValue: onChange,
      };

    case DIRECTIVES_VALUES.MULTIPLE:
      return {
        ...commonProps,
        defaultValues: defaultValue,
        multiple: true,
        onChange,
      };

    default:
      return commonProps;
  }
}

function emptyNode(node) {
  while (node.firstChild) {
    node.removeChild(node.firstChild);
  }
}

function getOnChangeHandler(selectBox, autocompleteContainer, onChange) {
  return (newValue) => {
    emptyNode(selectBox);

    const values = (Array.isArray(newValue) ? newValue : [newValue]).filter((item) => item.id);

    values.forEach((item) => {
      const option = browser.document.createElement('option');
      option.value = item.id;
      option.selected = true;
      option.appendChild(browser.document.createTextNode(item.value));

      selectBox.appendChild(option);
    });

    autocompleteContainer.setAttribute('data-is-dirty', 1);

    const event = new browser.window.Event('change');
    selectBox.form.dispatchEvent(event);

    onChange(values);
  };
}

function createAndInsertSpan(target) {
  const span = browser.document.createElement('span');
  target.parentNode.insertBefore(span, target);

  return span;
}

function createAndInsertSelectBox(target) {
  const selectBox = browser.document.createElement('select');
  selectBox.name = target.name;
  selectBox.multiple = true;
  target.parentNode.insertBefore(selectBox, target);

  return selectBox;
}

function getDataSource(
  url,
  autocompleteContainer,
  mapping = {},
  searchParameter = 'q',
  loader = null
) {
  return (search) => {
    if (loader) {
      loader.style.setProperty('opacity', 1);
    }

    const otherParametersAttribute = autocompleteContainer.getAttribute(
      'data-autocomplete-other-parameters'
    );
    const data = otherParametersAttribute ? JSON.parse(otherParametersAttribute) : {};
    data[searchParameter] = search;

    return ajaxGet(url, { data }).then((response) => {
      const allDatas = response.data || [];

      if (loader) {
        loader.style.setProperty('opacity', 0);
      }

      return allDatas.map((currentData) => ({
        id: currentData[mapping.id] || currentData.id,
        value: currentData[mapping.label] || currentData.value,
        image: currentData[mapping.image] || currentData.image,
      }));
    });
  };
}

export function autocompleteFactory(
  input,
  directiveValue,
  onChangeHandler = () => {},
  onClearHandler = () => {}
) {
  const dataProps = JSON.parse(input.getAttribute('data-props'));
  const span = createAndInsertSpan(input);
  span.id = `autocomplete_container__${input.name}`;

  const {
    autoInit,
    dataSourceUrl,
    mapping,
    searchParameter,
    defaultValue,
    defaultValues,
    ...otherDataProps
  } = dataProps;

  const loader = input.parentNode.querySelector('.js-loader');

  const dataSource = getDataSource(dataSourceUrl, span, mapping, searchParameter, loader);

  const defaultValueProp = getDefaultValue({ defaultValue, defaultValues }, directiveValue);

  const selectBox = createAndInsertSelectBox(input);

  const onChange = getOnChangeHandler(selectBox, span, onChangeHandler);
  onChange(defaultValueProp);

  const onClear = () => {
    emptyNode(selectBox);
    onClearHandler();
  };

  span.removeAttribute('data-is-dirty');

  input.removeAttribute('name');

  const suggestionRenderer = (suggestion) => {
    if (!suggestion.image) {
      return suggestion.value;
    }

    return (
      <span>
        <img alt="" className="autocompleteSuggestions__itemImage" src={suggestion.image} />
        {suggestion.value}
      </span>
    );
  };

  const root = createRoot(span);
  root.render(
    <AppProviders productionPrefix="autocomplete">
      <AutocompleteFromDom
        {...getComponentProps(
          directiveValue,
          input,
          dataSource,
          defaultValueProp,
          onChange,
          onClear,
          suggestionRenderer,
          otherDataProps
        )}
      />
    </AppProviders>
  );

  input.parentNode.removeChild(input);
}

function initAutocomplete(directiveValue) {
  const inputs = [...browser.document.querySelectorAll(`input[data-react="${directiveValue}"]`)];
  inputs.forEach((input) => {
    const dataProps = JSON.parse(input.getAttribute('data-props'));

    if (!dataProps.autoInit) {
      return;
    }

    autocompleteFactory(input, directiveValue);
  });
}

function init() {
  initAutocomplete(DIRECTIVES_VALUES.SIMPLE);
  initAutocomplete(DIRECTIVES_VALUES.MULTIPLE);
}

export default init;
